// Per spec/blocks.md §author (P-037.28 / D-136). One of 4 atomic Blocks
// that replace the prior `commentary` + `text-block` Blocks (dropped at
// the v9 → v10 migration). Renders an author byline via the existing
// AuthorByline Component (D-113 / D-032).
//
// **Field shape matches commentary's pre-D-136 byline fields:** required
// `authorName` + optional `authorTitle` + optional `authorPhotoUrl` +
// optional `authorSignatureUrl`. Photo is always round (the only style
// commentary used; the `photoStyle` enum was dropped at P-037.15 / D-115).
// AuthorByline's no-photo collapse arm handles the layout when
// `authorPhotoUrl` is unset.
//
// **Outer cell padding: 30 px L/R + 0 top + 0 bottom.** Same content-
// gutter convention as `headline` + `text` + `button`. AuthorByline's
// own `<table>` is left-aligned within the padded cell. Banks-confirmed
// at planning: all 4 atomic Blocks render LEFT-ALIGNED.
//
// **Auto-hide on empty `authorName`.** Required at the Zod layer
// (`min(1)`); defense-in-depth if persisted state slips through empty —
// AuthorByline gates its entire render on `name`, so an empty name
// produces an empty byline. Auto-hide makes the orchestrator-level
// skip explicit.

import { z } from 'zod'
import { defineBlock } from '../render/define-block'
import { html } from '../render'
import { authorByline, type AuthorBylineProps } from '../components/author-byline'
import { pickDefined } from '../components/helpers'

const optionsSchema = z.object({})

const inputsSchema = z.object({
  authorName: z.string().min(1),
  authorTitle: z.string().min(1).optional(),
  authorPhotoUrl: z.string().url().describe('imageUrl').optional(),
  authorSignatureUrl: z.string().url().describe('imageUrl').optional(),
})

export const author = defineBlock({
  id: 'author',
  label: 'Author',
  category: 'content',
  optionsSchema,
  inputsSchema,
  defaultOptions: {},
  defaultInputs: { authorName: 'Author Name' },
  autoHide: ({ inputs }) => inputs.authorName.length === 0,
  render: ({ inputs }) => {
    // `pickDefined` strips undefined-valued keys so the resulting Partial
    // spreads cleanly into a target with optional fields under
    // exactOptionalPropertyTypes (D-006).
    const bylineProps: AuthorBylineProps = {
      name: inputs.authorName,
      photoStyle: 'round',
      ...pickDefined({
        title: inputs.authorTitle,
        photoUrl: inputs.authorPhotoUrl,
        signatureUrl: inputs.authorSignatureUrl,
      }),
    }
    const bylineHtml = authorByline(bylineProps)
    // prettier-ignore
    return html`<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%"><tr><td style="padding: 0 30px;">${bylineHtml}</td></tr></table>`
  },
})
